import { z } from 'zod';
import { IdSchema } from './encoding.js';
import { PermissionActionSchema, SensitivityCategorySchema } from './identity.js';
/**
 * Platform administration of governed configuration (SDD §15; FRD-CORE-037, CORE-042, CORE-043,
 * USR-015, USR-020): configuration parameters, configurable list values, number sequences,
 * sensitive-field designations and segregation-of-duties rules. Every change is a privileged,
 * audited action (SDD §11.5).
 */
const DateTimeSchema = z.iso.datetime({ offset: true });
// ------------------------------------------------------------------ Configuration parameters
/** One declared parameter with its value now in force (SDD §15.2). Declarations live in code. */
export const ConfigurationParameterSummarySchema = z.object({
    key: z.string(),
    owningModule: z.string(),
    description: z.string(),
    requiredBeforeUse: z.boolean(),
    /** The declared value schema as JSON Schema, so the editor can offer the right control. */
    valueSchema: z.record(z.string(), z.unknown()),
    /** Absent: never set. A required parameter that is unset blocks the function that needs it. */
    currentValue: z.unknown().optional(),
    effectiveFrom: DateTimeSchema.nullable(),
    /** A value recorded with a future effective date, not yet in force. */
    scheduledValue: z.unknown().optional(),
    scheduledFrom: DateTimeSchema.nullable(),
    /** The scheduled change returns the parameter to "not set" (no `scheduledValue`). */
    scheduledUnset: z.boolean(),
});
export const ConfigurationParameterHistoryEntrySchema = z.object({
    /** A value, a return to "not set", or the cancellation of a scheduled change. */
    kind: z.enum(['value', 'unset', 'cancellation']),
    value: z.unknown(),
    /** For a cancellation: when it was made. */
    effectiveFrom: DateTimeSchema,
    /** A scheduled value or unset withdrawn before it took effect. */
    cancelled: z.boolean(),
    /** For a cancellation: when the withdrawn change would have taken effect. */
    cancelsEffectiveFrom: DateTimeSchema.nullable(),
    setBy: z.string().nullable(),
    setAt: DateTimeSchema,
});
export const SetConfigurationParameterRequestSchema = z.strictObject({
    value: z.unknown(),
    /** Omitted: in force at once. A future date schedules the change (SDD §15.2). */
    effectiveFrom: DateTimeSchema.optional(),
    reason: z.string().min(1).max(500).optional(),
});
/** Returns an optional parameter to "not set", at once or from a future instant. */
export const UnsetConfigurationParameterRequestSchema = z.strictObject({
    effectiveFrom: DateTimeSchema.optional(),
    reason: z.string().min(1).max(500).optional(),
});
/** Cancels the scheduled change the caller saw, named by its `scheduledFrom`. */
export const CancelScheduledConfigurationRequestSchema = z.strictObject({
    scheduledFrom: DateTimeSchema,
    reason: z.string().min(1).max(500).optional(),
});
// ------------------------------------------------------------------ Configurable list values
export const ListDeclarationSummarySchema = z.object({
    listCode: z.string(),
    owningModule: z.string(),
    description: z.string(),
    activeValues: z.number().int(),
});
export const ListValueSchema = z.object({
    id: IdSchema,
    listCode: z.string(),
    valueCode: z.string(),
    label: z.string(),
    sortOrder: z.number().int(),
    status: z.enum(['active', 'inactive']),
    version: z.number().int(),
});
export const CreateListValueRequestSchema = z.strictObject({
    valueCode: z
        .string()
        .regex(/^[a-z0-9][a-z0-9_]*$/, 'use lower-case letters, digits and underscores')
        .max(50),
    label: z.string().trim().min(1).max(100),
    sortOrder: z.number().int().min(0).max(100000).optional(),
});
export const UpdateListValueRequestSchema = z.strictObject({
    label: z.string().trim().min(1).max(100).optional(),
    sortOrder: z.number().int().min(0).max(100000).optional(),
    status: z.enum(['active', 'inactive']).optional(),
});
// ------------------------------------------------------------------ Number sequences
/** A series a module issues numbers from, whether or not it has been configured yet. */
export const NumberSequenceSummarySchema = z.object({
    code: z.string(),
    owningModule: z.string().nullable(),
    description: z.string().nullable(),
    /** False: the module's numbered records cannot be created until an administrator defines it. */
    configured: z.boolean(),
    id: IdSchema.nullable(),
    scopeKey: z.string(),
    format: z.string().nullable(),
    /** The next number to be issued, as a digit string (it may exceed 2^53). */
    nextValue: z.string().nullable(),
    /** What the next number will look like with the current format. */
    nextPreview: z.string().nullable(),
    status: z.enum(['active', 'inactive']).nullable(),
    version: z.number().int().nullable(),
});
export const DefineNumberSequenceRequestSchema = z.strictObject({
    code: z
        .string()
        .regex(/^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)+$/)
        .max(100),
    format: z.string().min(1).max(100),
    /** Where the series starts, as a digit string. Defaults to 1. */
    startAt: z
        .string()
        .regex(/^[1-9]\d{0,17}$/)
        .optional(),
});
export const UpdateNumberSequenceRequestSchema = z.strictObject({
    format: z.string().min(1).max(100).optional(),
    status: z.enum(['active', 'inactive']).optional(),
});
export const NumberFormatPreviewSchema = z.object({ format: z.string(), preview: z.string() });
// ------------------------------------------------------------------ Sensitive-field designations
export const DesignatableFieldSchema = z.object({ field: z.string(), label: z.string() });
/** A record type whose fields may be designated, with those fields (declared by its module). */
export const DesignatableRecordTypeSchema = z.object({
    recordType: z.string(),
    displayName: z.string(),
    fields: z.array(DesignatableFieldSchema),
});
export const SensitiveFieldDesignationSchema = z.object({
    id: IdSchema,
    recordType: z.string(),
    recordTypeName: z.string(),
    field: z.string(),
    fieldLabel: z.string().nullable(),
    sensitivityCategory: SensitivityCategorySchema,
    createdAt: DateTimeSchema,
});
export const DesignateFieldRequestSchema = z.strictObject({
    recordType: z.string().min(1).max(100),
    field: z.string().min(1).max(100),
    sensitivityCategory: SensitivityCategorySchema,
});
// ------------------------------------------------------------------ Segregation-of-duties rules
/** A record type SoD can be evaluated on: its module supplies the chain of acting users. */
export const SodRecordTypeSchema = z.object({
    recordType: z.string(),
    displayName: z.string(),
    /** Actions declared on the record type, which may be controlled. */
    actions: z.array(PermissionActionSchema),
    /** Actions the chain provider can name as conflicting, e.g. `create` or `<type>:<action>`. */
    conflictingActions: z.array(z.string()),
});
export const SodRuleSchema = z.object({
    id: IdSchema,
    code: z.string(),
    recordType: z.string(),
    recordTypeName: z.string(),
    controlledAction: z.string(),
    conflictingAction: z.string(),
    requiredIndependentPermission: z.string().nullable(),
    status: z.enum(['active', 'inactive']),
    version: z.number().int(),
});
export const DefineSodRuleRequestSchema = z.strictObject({
    code: z
        .string()
        .regex(/^[a-z][a-z0-9_]*$/, 'use lower snake case')
        .max(60),
    recordType: z.string().min(1).max(100),
    controlledAction: z.string().min(1).max(100),
    conflictingAction: z.string().min(1).max(100),
    requiredIndependentPermission: z.string().min(1).max(100).optional(),
});
export const UpdateSodRuleRequestSchema = z.strictObject({
    status: z.enum(['active', 'inactive']).optional(),
    requiredIndependentPermission: z.string().min(1).max(100).nullable().optional(),
});
// ------------------------------------------------------------------ Audit trail
export const AuditEventTypeSchema = z.enum([
    'create',
    'change',
    'status_change',
    'approval',
    'posting',
    'access',
    'unmasked_access',
    'export',
    'custody_change',
    'scan',
    'config_change',
    'sod_block',
    'auth_failure',
    'privileged_action',
    'ai_assistance_used',
]);
export const AuditFieldChangeSchema = z.object({
    field: z.string(),
    previousValue: z.string().nullable(),
    newValue: z.string().nullable(),
    /** The value is a designated sensitive field the viewer may not see unmasked (SDD §13.4). */
    masked: z.boolean(),
});
export const AuditRecordSchema = z.object({
    id: IdSchema,
    occurredAt: DateTimeSchema,
    eventType: AuditEventTypeSchema,
    actorUserId: IdSchema.nullable(),
    actorName: z.string().nullable(),
    actorProcess: z.string().nullable(),
    subjectRecordType: z.string().nullable(),
    subjectRecordTypeName: z.string().nullable(),
    subjectRecordId: z.string().nullable(),
    subjectLabel: z.string().nullable(),
    approvalStepId: IdSchema.nullable(),
    correlationId: z.string().nullable(),
    context: z.record(z.string(), z.unknown()).nullable(),
    fieldChanges: z.array(AuditFieldChangeSchema),
});
export const AuditRecordTypeSchema = z.object({ code: z.string(), displayName: z.string() });
// ------------------------------------------------------------------ Global search
/** One record found by global search (FRD-CORE-038): codes and names only. */
export const GlobalSearchHitSchema = z.object({
    recordType: z.string(),
    recordTypeName: z.string(),
    recordId: z.string(),
    title: z.string(),
    subtitle: z.string().nullable(),
});
//# sourceMappingURL=administration.js.map